import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getAppLink, saveAppSettings } from "../api/AppLinkApi";
import { useParams, useSearchParams } from "react-router-dom";
import HubspotSetting from "./HubspotSetting";
import JiraSetting from "./JiraSetting";
import LayoutAction from "../actions/LayoutAction";
import MailchimpSetting from "./MailchimpSetting";
import GoogleSetting from "./GoogleSetting";
import ImportSetting from "./ImportSetting";
import ZapierSetting from "./ZapierSetting";
import AzureSetting from "./AzureSetting";
import MobileAppSetting from "./MobileAppSetting";
import FacebookSetting from "./FacebookSetting";
import ThreadsSetting from "./ThreadsSetting";
import InstagramSetting from "./InstagramSetting";
import TwitterSetting from "./TwitterSetting";
import LinkedinSetting from "./LinkedinSetting";
import CanvaSetting from "./CanvaSetting";
import { useTranslation } from "react-i18next";
import { AlertCircle, PackageMinus, PackagePlus, SquareX } from "lucide-react";
import { getBaseUrl } from "../api/api";

export const AppInfo = ({ appInfo, onConnectOrDisconnect, onClose, statusActionLabel }: any) => {
  const { projectId }: any = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const appUrl = getBaseUrl();

  const [appData, setAppData] = useState(appInfo);
  const [showAccessDeniedError, setShowAccessDeniedError] = useState(false);
  const accessDeniedError = searchParams.get("error") === "access_denied";

  useEffect(() => {
    if (appInfo) {
      loadAppData();
    }
  }, [appInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (accessDeniedError) {
      setShowAccessDeniedError(true);
      dispatch(
        LayoutAction.setNotification({
          title: t("App"),
          type: "error",
          message: t(
            "Access was denied. You did not approve the connection or the authorization was cancelled."
          ),
        })
      );
      searchParams.delete("error");
      searchParams.delete("error_description");
      setSearchParams(searchParams, { replace: true });
    }
  }, [accessDeniedError]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAppData = () => {
    getAppLink(appInfo.app).then((data) => {
      setAppData({
        ...appInfo,
        connected: data.connected,
        accessToken: data.accessToken,
        ...data,
      });
    });
  };

  const handleAppSettings = ({ app, fieldId, fieldValue }) => {
    const appSettings = {
      [fieldId]: fieldValue,
    };
    saveAppSettings(app, appSettings)
      .then((newAppData) => {
        dispatch(
          LayoutAction.setNotification({
            title: t("App"),
            type: "success",
            message: t("Settings are updated."),
          })
        );
        setAppData({
          ...appData,
          ...newAppData,
        });
      })
      .catch(() => {
        dispatch(
          LayoutAction.setNotification({
            title: t("App"),
            type: "error",
            message: t("App is unsupported."),
          })
        );
      });
  };

  const connectionStatus = () => {
    if (appInfo.connected) {
      return (
        <div className="px-2 py-1 w-max rounded-full text-xs bg-[#CCFBF1]">
          <span className="inline-block w-2 h-2 rounded-full bg-[#115E59]"></span>
          <span className="ml-2 text-[#115E59]">{t("Good Connection")}</span>
        </div>
      );
    } else {
      return (
        <div className="px-2 py-1 w-max rounded-full text-xs bg-[#FEE2E2]">
          <span className="inline-block w-2 h-2 rounded-full bg-[#991B1B]"></span>
          <span className="ml-2 text-[#991B1B]">{t("No Connection")}</span>
        </div>
      );
    }
  };

  const renderAppSetting = () => {
    switch (appData.app) {
      case "hubspot":
        return (
          <HubspotSetting
            projectId={projectId}
            appInfo={appData}
            onChange={handleAppSettings}
            connectionStatus={connectionStatus}
          />
        );
      case "jsm":
        return (
          <JiraSetting
            ruleId={`${projectId}-${appInfo.app}`}
            appInfo={appData}
            onChange={handleAppSettings}
            connectionStatus={connectionStatus}
          />
        );
      case "mailchimp":
        return (
          <MailchimpSetting
            appInfo={appData}
            onChange={handleAppSettings}
            connectionStatus={connectionStatus}
          />
        );
      case "google":
        return <GoogleSetting appInfo={appData} />;
      case "import_csv":
        return (
          <ImportSetting
            ruleId={`${projectId}-import-csv`}
            projectId={projectId}
            appInfo={appData}
          />
        );
      case "zapier":
        return <ZapierSetting appInfo={appData} />;
      case "mobile":
        return <MobileAppSetting appInfo={appData} />;
      case "azure":
        return <AzureSetting appInfo={appData} />;
      case "social_content_planner":
        return null;
      case "facebook":
        return <FacebookSetting appInfo={appData} onReloadApp={() => loadAppData()} />;
      case "threads":
        return <ThreadsSetting appInfo={appData} />;
      case "instagram":
        return <InstagramSetting appInfo={appData} onReloadApp={() => loadAppData()} />;
      case "twitter":
        return <TwitterSetting appInfo={appData} />;
      case "linkedin":
        return <LinkedinSetting appInfo={appData} />;
      case "canva":
        return <CanvaSetting appInfo={appData} />;
    }
  };

  return (
    <div className={"space-y-4"}>
      {showAccessDeniedError && (
        <div
          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA]"
          role="alert"
        >
          <AlertCircle className="w-4 h-4 flex-none" />
          <span>
            {t(
              "Access was denied. You did not approve the connection or the authorization was cancelled."
            )}
          </span>
          <button
            type="button"
            onClick={() => setShowAccessDeniedError(false)}
            className="ml-auto p-1 rounded hover:bg-[#FECACA] focus:outline-none focus:ring-2 focus:ring-[#991B1B]"
            aria-label={t("Dismiss")}
          >
            <SquareX className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="flex items-center justify-between mb-10">
        <div className="flex items-center space-x-4">
          <div>
            <img
              className="w-8 h-8 object-contain flex-none"
              src={`${appUrl}${appData.imageUrl}`}
              alt=""
            />
          </div>
          <div>
            <div className="text-lg font-semibold leading-6 text-gray-900">{appData.name}</div>
            <div className="mt-1 text-base leading-5 text-gray-500">{appData.description}</div>
          </div>
        </div>
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => onConnectOrDisconnect(appData)}
            type="button"
            className="flex items-center rounded-md px-3 py-2 text-xs font-semibold shadow-xs border-1 border-gray-300 border-solid hover:bg-blue-500 hover:text-white focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            {appData.connected ? (
              <PackageMinus className="w-4 h-4 mr-2" />
            ) : (
              <PackagePlus className="w-4 h-4 mr-2" />
            )}
            {statusActionLabel(appData)}
          </button>
          <button
            onClick={onClose}
            type="button"
            className="flex items-center rounded-md px-3 py-2 text-xs font-semibold shadow-xs border-1 border-gray-300 border-solid hover:bg-blue-500 hover:text-white focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            <SquareX className="w-4 h-4 mr-2" />
            {t("Close")}
          </button>
        </div>
      </div>
      {renderAppSetting()}
    </div>
  );
};

export default AppInfo;
